import { initialFlowState } from "./flowReducer";
import { GOAL_IDS } from "../steps/registry";

/**
 * sessionStorage persistence for the onboarding flow.
 *
 * Kept separate from the provider so the awkward parts — corrupt JSON, schema
 * drift, storage that throws — are testable without rendering anything.
 *
 * sessionStorage rather than localStorage is deliberate: a refresh mid-signup
 * should not cost the member ten screens of input, but an abandoned signup
 * should not still be waiting for them next week.
 */

/**
 * The version suffix matters. If the shape of flow state changes, bump it: a
 * new key means an old payload is simply never read, rather than half-read into
 * a shape the reducer no longer understands.
 */
export const STORAGE_KEY = "echo.onboarding.flow.v1";

/**
 * Storage can throw rather than merely be unavailable — Safari private browsing
 * throws on write, and an embedded webview can throw on access. Persistence is
 * a convenience, so every failure degrades to "no saved state" instead of
 * taking the flow down with it.
 */
const withStorage = (fn, fallback = null) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage)
      return fallback;
    return fn(window.sessionStorage);
  } catch {
    return fallback;
  }
};

/**
 * Rebuild state from a stored payload, keeping only keys the current shape
 * declares.
 *
 * Unknown keys are dropped and missing keys fall back to their initial value,
 * so a payload written by an older or newer build can never introduce a field
 * the reducer does not handle, and can never leave one absent.
 */
export const sanitizeFlowState = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return initialFlowState;
  }

  const restored = { ...initialFlowState };

  for (const key of Object.keys(initialFlowState)) {
    if (!(key in raw)) continue;

    const value = raw[key];
    const expected = initialFlowState[key];

    if (Array.isArray(expected)) {
      if (Array.isArray(value)) restored[key] = value;
      continue;
    }
    if (expected !== null && typeof expected === "object") {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        restored[key] = value;
      }
      continue;
    }
    if (typeof expected === "boolean") {
      if (typeof value === "boolean") restored[key] = value;
      continue;
    }

    // Everything else (pathway, dob, email) starts as null and holds a string.
    //
    // Checked rather than passed through: sessionStorage is editable, and the
    // step guards ask `Boolean(dob)`. An object or an array there is truthy, so
    // an unchecked value would open a step whose data was never collected. This
    // is not a security boundary — the server validates — but the flow should
    // not be reachable in a state its own screens cannot render.
    if (typeof value === "string" || value === null) {
      restored[key] = value;
    }
  }

  // Membership, not just shape. The loop above accepts `goals` because it is an
  // array, and `["bogus_goal"]` is a perfectly good array — but a goal outside
  // `GOAL_IDS` plans no sub-flow, has no screen and still counts toward the
  // sub-flow step's weight, so the bar reserves a screen the member never sees.
  // The same applies to a duplicate, and to a `goalData` key for a goal that is
  // not selected.
  //
  // Reset rather than repair: which of the two keys is wrong is not knowable
  // from here, and a half-corrected selection is harder to reason about than a
  // fresh one. Only these two are cleared — the account details behind them are
  // still good, so the member lands back on the goal picker rather than at the
  // start with their email to verify again.
  const goalsConsistent =
    new Set(restored.goals).size === restored.goals.length &&
    restored.goals.every((goal) => GOAL_IDS.includes(goal)) &&
    Object.keys(restored.goalData).every((goal) =>
      restored.goals.includes(goal),
    );

  if (!goalsConsistent) {
    restored.goals = [];
    restored.goalData = {};
  }

  return restored;
};

/** Read persisted state, falling back to a fresh flow for anything unusable. */
export const loadFlowState = () =>
  withStorage((storage) => {
    const stored = storage.getItem(STORAGE_KEY);
    if (!stored) return initialFlowState;
    try {
      return sanitizeFlowState(JSON.parse(stored));
    } catch {
      // Corrupt payload: start clean rather than leave it to fail later.
      return initialFlowState;
    }
  }, initialFlowState);

export const saveFlowState = (state) =>
  withStorage((storage) => {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  }, false);

/**
 * Seed V2 onboarding for an incomplete existing account, restoring a same-email
 * draft when available. DOB was captured at registration, so recovery skips it
 * even in a fresh browser. Completed accounts go directly to the dashboard.
 */
export const startExistingAccountFlow = ({
  email,
  emailVerified = false,
  reset = false,
} = {}) => {
  const current = loadFlowState();
  const normalizedEmail = email?.trim().toLowerCase() ?? null;
  const localMatch =
    !reset &&
    Boolean(normalizedEmail) &&
    current.email?.trim().toLowerCase() === normalizedEmail;
  const restored = localMatch ? current : initialFlowState;
  const state = {
    ...initialFlowState,
    ...restored,
    pathway: "person",
    existingAccount: true,
    email: normalizedEmail ?? restored.email,
    emailVerified,
  };
  saveFlowState(state);
  return state;
};

/** Called on completion, and on an explicit reset. */
export const clearFlowState = () =>
  withStorage((storage) => {
    storage.removeItem(STORAGE_KEY);
    return true;
  }, false);
